const ANTHROPIC_ENDPOINT = "/api/anthropic-proxy";
const OPENAI_ENDPOINT = "/api/openai-proxy";

import { supabase } from "./supabase";

/** Récupère le token JWT Supabase de la session courante. */
async function getAuthToken() {
  if (!supabase) return null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch {
    return null;
  }
}

const OPENAI_MODELS = ["gpt-4o-mini", "gpt-4o", "gpt-4-turbo"];

function isOpenAI(model) {
  return OPENAI_MODELS.some(m => model.startsWith(m));
}

function buildAnthropicBody({ model, maxTokens, messages, tools, toolChoice, system, temperature }) {
  const body = { model, max_tokens: maxTokens, messages };
  if (system) body.system = system;
  if (temperature !== undefined) body.temperature = temperature;
  if (tools) body.tools = tools;
  if (toolChoice) body.tool_choice = toolChoice;
  return body;
}

function buildOpenAIBody({ model, maxTokens, messages, system, temperature }) {
  const oaiMessages = [];
  if (system) oaiMessages.push({ role: "system", content: system });
  for (const m of messages) {
    if (typeof m.content === "string") {
      oaiMessages.push({ role: m.role, content: m.content });
    } else if (Array.isArray(m.content)) {
      const text = m.content.filter(c => c.type === "text").map(c => c.text).join("\n");
      oaiMessages.push({ role: m.role, content: text });
    } else {
      oaiMessages.push(m);
    }
  }
  const body = { model, max_tokens: maxTokens, messages: oaiMessages };
  if (temperature !== undefined) body.temperature = temperature;
  return body;
}

function normalizeOpenAIResponse(data) {
  const choice = data.choices?.[0];
  if (!choice) return data;
  return {
    content: [{ type: "text", text: choice.message?.content || "" }],
    model: data.model,
    usage: data.usage,
    _provider: "openai",
  };
}

const MAX_RETRIES = 2;
const RETRY_DELAYS = [1500, 4000];
const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

function classifyError(status, data) {
  if (status === 401) return "Authentification expirée, reconnecte-toi.";
  if (status === 403) return "Accès refusé au service IA.";
  if (status === 429 && data?.message) return data.message;
  if (status === 429) return "Trop de requêtes, réessaie dans quelques secondes.";
  if (status === 400) {
    const msg = typeof data?.error === "string" ? data.error : data?.error?.message || "";
    if (msg.includes("not allowed")) return "Modèle IA non autorisé.";
    return `Requête invalide : ${msg || "vérifie les paramètres."}`;
  }
  if (status >= 500) return "Service IA temporairement indisponible, réessaie.";
  if (data?.error) {
    const msg = typeof data.error === "string" ? data.error : data.error?.message || JSON.stringify(data.error);
    return msg;
  }
  return `Erreur inattendue (HTTP ${status}).`;
}

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms));
}

export async function callAI({ model = "claude-sonnet-4-20250514", maxTokens = 400, messages, tools, toolChoice, system, temperature, _action }) {
  const useOpenAI = isOpenAI(model);

  const endpoint = useOpenAI ? OPENAI_ENDPOINT : ANTHROPIC_ENDPOINT;
  const body = useOpenAI
    ? buildOpenAIBody({ model, maxTokens, messages, system, temperature })
    : buildAnthropicBody({ model, maxTokens, messages, tools, toolChoice, system, temperature });

  const payload = JSON.stringify(body);
  const authToken = await getAuthToken();
  if (!authToken) console.warn("[callAI] No auth token — proxy will reject with 401");
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
  if (_action) headers["x-athleos-action"] = _action;

  let lastError = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await sleep(RETRY_DELAYS[attempt - 1] || 4000);
    }

    let res;
    try {
      res = await fetch(endpoint, {
        method: "POST",
        headers,
        body: payload,
      });
    } catch (networkErr) {
      lastError = new Error("Réseau indisponible, vérifie ta connexion.");
      if (attempt < MAX_RETRIES) continue;
      throw lastError;
    }

    let text;
    try {
      text = await res.text();
    } catch {
      lastError = new Error(`Réponse illisible du serveur (HTTP ${res.status}).`);
      if (attempt < MAX_RETRIES && RETRYABLE_STATUSES.has(res.status)) continue;
      throw lastError;
    }

    if (!text || !text.trim()) {
      lastError = new Error(`Réponse vide du serveur (HTTP ${res.status}), réessaie.`);
      if (attempt < MAX_RETRIES) continue;
      throw lastError;
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      lastError = new Error(`Réponse invalide du serveur (HTTP ${res.status}): ${text.slice(0, 200)}`);
      if (attempt < MAX_RETRIES) continue;
      throw lastError;
    }

    if (!res.ok || data.error) {
      const msg = classifyError(res.status, data);
      lastError = new Error(msg);
      if (data?.error === "blocked" || data?.error === "rate_limit") throw lastError;
      if (attempt < MAX_RETRIES && RETRYABLE_STATUSES.has(res.status)) continue;
      throw lastError;
    }

    return useOpenAI ? normalizeOpenAIResponse(data) : data;
  }

  throw lastError || new Error("Échec après plusieurs tentatives.");
}

export function getTextFromResponse(data) {
  return data.content?.[0]?.text || "";
}

export function getToolUse(data, toolName) {
  return (data.content || []).find((c) => c.type === "tool_use" && c.name === toolName);
}
